/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectRoleItem.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectRoleItem - Frontend UI module: ProjectRoleItem.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Пропсы showConcurrentLabels и isLast для групповой вёрстки этапов.
 * LAST_CHANGE: 2026-07-14 - UX: мини-прогресс-бар загрузки вместо текстового процента.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций и парные END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';

/* START_METHOD_ShortNameChip */
/* START_CONTRACT: ShortNameChip
 * PURPOSE: Отобразить чип короткого имени роли с цветом, вычисленным по названию.
 * INPUTS: { label: string }.
 * OUTPUTS: { JSX.Element - чип }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: ShortNameChip
 */
function ShortNameChip({ label }) {
  const color = getColorByName(label);

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: color,
        color: '#fff',
      }}
    />
  );
}
/* END_METHOD_ShortNameChip */

/* START_METHOD_ProjectRoleItem */
/* START_CONTRACT: ProjectRoleItem
 * PURPOSE: Отобразить строку проектной роли с загрузкой, совместными ролями и действиями.
 * INPUTS: { role, allRoles, onEdit, onDelete, showConcurrentLabels?, isLast? }.
 * OUTPUTS: { JSX.Element - строка }.
 * SIDE_EFFECTS: нет (делегирует onEdit/onDelete).
 * LINKS: M-FE-ROLES
 * END_CONTRACT: ProjectRoleItem
 */
export default function ProjectRoleItem({
  role,
  allRoles,
  onEdit,
  onDelete,
  showConcurrentLabels = true,
  isLast = false,
}) {
  const concurrentLabels = showConcurrentLabels
    ? role.getConcurrentRoleLabels(allRoles)
    : [];
  const roleColor = getColorByName(role.name);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" noWrap>
            {role.name}
          </Typography>
          <ShortNameChip label={role.shortName} />
          <RoleWorkloadBar
            value={role.workloadPercent}
            accentColor={roleColor}
            size="md"
          />
        </Box>
        {concurrentLabels.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Совместно:
            </Typography>
            {concurrentLabels.map((label) => (
              <ShortNameChip key={label} label={label} />
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2, flexShrink: 0 }}>
        <Tooltip title="Редактировать">
          <IconButton size="small" onClick={() => onEdit(role)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Удалить">
          <IconButton size="small" color="error" onClick={() => onDelete(role)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
/* END_METHOD_ProjectRoleItem */
